import { useEffect, useRef } from 'react';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { javascript } from '@codemirror/lang-javascript';
import { cmTheme, cmHighlight, type BuildExtensionsOpts } from './cmSetup';
import {
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
} from '@codemirror/view';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import {
  bracketMatching,
  syntaxHighlighting,
} from '@codemirror/language';
import { closeBrackets } from '@codemirror/autocomplete';

interface TsxEditorProps {
  value: string;
  onChange?: (next: string) => void; // omitted ⇒ read-only
  readOnly?: boolean;
  /** Exposed so TsxInsertMenu can insert at cursor. */
  viewRef?: React.MutableRefObject<EditorView | null>;
}

function buildTsxExtensions({ readOnly, onChange }: Pick<BuildExtensionsOpts, 'readOnly' | 'onChange'>) {
  const base = [
    lineNumbers(),
    javascript({ jsx: true, typescript: true }),
    bracketMatching(),
    cmTheme,
    syntaxHighlighting(cmHighlight),
    EditorView.lineWrapping,
  ];

  if (readOnly) {
    base.push(
      EditorState.readOnly.of(true),
      EditorView.editable.of(false),
    );
    return base;
  }

  base.push(
    highlightActiveLine(),
    highlightActiveLineGutter(),
    history(),
    closeBrackets(),
    keymap.of([
      ...defaultKeymap,
      ...historyKeymap,
      indentWithTab,
    ]),
  );

  if (onChange) {
    base.push(
      EditorView.updateListener.of((update) => {
        if (update.docChanged) onChange(update.state.doc.toString());
      }),
    );
  }

  return base;
}

export function TsxEditor({ value, onChange, readOnly, viewRef }: TsxEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const internalViewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const ro = !onChange || !!readOnly;

  useEffect(() => {
    if (!hostRef.current) return;
    const view = new EditorView({
      parent: hostRef.current,
      state: EditorState.create({
        doc: value,
        extensions: buildTsxExtensions({
          readOnly: ro,
          onChange: ro ? undefined : (next) => onChangeRef.current?.(next),
        }),
      }),
    });
    internalViewRef.current = view;
    if (viewRef) viewRef.current = view;
    return () => {
      view.destroy();
      internalViewRef.current = null;
      if (viewRef) viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ro]);

  // External value sync (controlled pattern).
  useEffect(() => {
    const view = internalViewRef.current;
    if (!view) return;
    if (value === view.state.doc.toString()) return;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
    });
  }, [value]);

  return (
    <div
      ref={hostRef}
      className="w-full h-full overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    />
  );
}
